<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end bills-info-offcanvas"
    tabindex="-1"
    id="offcanvasPOSBillInfo"
    ref="offcanvasInfo"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ $t('Bill Detail') }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div v-if="loading" class="offcanvas-body p-3" style="background:#f8f9fa;">
      <p-o-s-page-skeleton variant="bills" :show-header="false" />
    </div>

    <div v-else-if="bill" class="offcanvas-body p-0">
      <!-- Hero Header -->
      <div class="bills-hero px-4 py-3">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="text-muted small">{{ $t('Bill No.') }}</div>
            <div class="fs-4 fw-bold">{{ bill.bill_no }}</div>
            <div class="text-muted small mt-1">
              <i class="fa-regular fa-clock me-1"/>
              {{ $t('Last Update') }}: {{ formatDateTime(bill.updated_at) }}
            </div>
          </div>
          <div class="text-end">
            <span class="badge px-3 py-2 fs-7" :class="'bg-' + statusColor">
              {{ $t(statusLabel) }}
            </span>
            <div class="fs-4 fw-bold mt-2">
              {{ $storeIndex.formatThaiCurrency(toNumber(bill.total)) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Bill Information -->
      <div class="card m-3 mb-0 bills-card">
        <div class="card-header">
          <i class="fa-solid fa-circle-info me-2 text-theme" />
          {{ $t('Bill Information') }}
        </div>
        <div class="card-body pb-0">
          <div class="row" style="font-size: 13px;">
            <div class="col-6 col-md-3 mb-3">
              <b>
                <i class="fa-solid fa-code-branch me-1" />
                {{ $t('Branch') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-3 mb-3">
              {{ bill.branch || '-' }}
            </div>

            <div class="col-6 col-md-3 mb-3">
              <b>
                <i class="fa-solid fa-store me-1" />
                {{ $t('Source') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-3 mb-3">
              {{ sourceText }}
            </div>

            <template v-if="bill.table_name">
              <div class="col-6 col-md-3 mb-3">
                <b>
                  <i class="fa-solid fa-chair me-1" />
                  {{ $t('Table') + ':' }}
                </b>
              </div>
              <div class="col-6 col-md-3 mb-3">
                {{ bill.table_name }}
              </div>
            </template>

            <template v-if="bill.customer_name">
              <div class="col-6 col-md-3 mb-3">
                <b>
                  <i class="fa-solid fa-user me-1" />
                  {{ $t('Customer') + ':' }}
                </b>
              </div>
              <div class="col-6 col-md-3 mb-3">
                {{ bill.customer_name }}
                <div v-if="bill.customer_phone" class="text-muted">
                  <a :href="'tel:' + bill.customer_phone">{{ bill.customer_phone }}</a>
                </div>
              </div>
            </template>

            <div class="col-6 col-md-3 mb-3">
              <b>
                <i class="fa-solid fa-user-pen me-1" />
                {{ $t('Created By') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-3 mb-3">
              {{ bill.created_by || '-' }}
              <div class="text-muted">
                {{ formatDateTime(bill.created_at) }}
              </div>
            </div>

            <template v-if="bill.closed_at">
              <div class="col-6 col-md-3 mb-3">
                <b>
                  <i class="fa-solid fa-circle-check me-1" />
                  {{ $t('_Bill.Closed By') + ':' }}
                </b>
              </div>
              <div class="col-6 col-md-3 mb-3">
                {{ bill.closed_by || '-' }}
                <div class="text-muted">
                  {{ formatDateTime(bill.closed_at) }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Items -->
      <div class="card m-3 mb-0 bills-card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div>
            <i class="fa-solid fa-list me-2 text-theme" />
            {{ $t('Items') }}
          </div>
          <span class="badge bg-light text-dark">
            {{ totalItemQuantity }} {{ $t('items') }}
          </span>
        </div>
        <div class="card-body p-0">
          <div
            v-for="(item, idx) in bill.items"
            :key="item.id"
            class="bills-item-row"
            :class="{ 'has-divider': idx > 0 }"
          >
            <div class="bills-item-image">
              <img
                v-if="item.image_url"
                :src="item.image_url"
                :alt="item.name"
              >
              <div v-else class="bills-item-placeholder">
                <i class="fa-regular fa-image"/>
              </div>
              <span class="bills-item-qty">×{{ item.quantity }}</span>
            </div>

            <div class="bills-item-body">
              <div class="bills-item-head">
                <div class="bills-item-name">
                  <span v-if="item.is_reward" class="badge bg-warning text-dark me-1" style="font-size: 0.65rem;">
                    <i class="fa-solid fa-gift me-1"></i>{{ $t('Reward') }}
                  </span>{{ $ln(item) }}
                </div>
                <div class="bills-item-total">
                  {{ $storeIndex.formatThaiCurrency(toNumber(item.total)) }}
                </div>
              </div>
              <div class="bills-item-sub">
                {{ $storeIndex.formatThaiCurrency(toNumber(item.unit_price)) }}
                <span class="bills-item-sub-dot">·</span>
                {{ $t('Qty') }} {{ item.quantity }}
              </div>

              <div v-if="item.options && item.options.length" class="bills-item-chips">
                <span
                  v-for="o in item.options"
                  :key="o.id"
                  class="bills-chip bills-chip-option"
                >
                  + {{ $t(o.name) }}
                </span>
              </div>

              <div v-if="item.remark" class="bills-item-note">
                <i class="fa-regular fa-comment me-1"/>{{ item.remark }}
              </div>

              <div v-if="toNumber(item.discount) > 0" class="bills-item-discount">
                <i class="fa-solid fa-tag me-1"/>
                {{ $t('Discount') }} -{{ $storeIndex.formatThaiCurrency(toNumber(item.discount)) }}
                <span v-if="item.discount_reason" class="text-muted">({{ item.discount_reason }})</span>
              </div>

              <div v-if="toNumber(item.cancelled_quantity) > 0 || toNumber(item.refunded_quantity) > 0" class="bills-item-chips">
                <span v-if="toNumber(item.cancelled_quantity) > 0" class="bills-chip bills-chip-danger">
                  <i class="fa-solid fa-ban me-1"/>
                  {{ $t('Cancelled') }} ×{{ item.cancelled_quantity }}
                  <span v-if="item.cancelled_reason" class="opacity-75"> — {{ item.cancelled_reason }}</span>
                </span>
                <span v-if="toNumber(item.refunded_quantity) > 0" class="bills-chip bills-chip-warning">
                  <i class="fa-solid fa-rotate-left me-1"/>
                  {{ $t('Refunded') }} ×{{ item.refunded_quantity }}
                  <span v-if="item.refund_reason" class="opacity-75"> — {{ item.refund_reason }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div class="card m-3 mb-0 bills-card">
        <div class="card-header">
          <i class="fa-solid fa-calculator me-2 text-theme" />
          {{ $t('Summary') }}
        </div>
        <div class="card-body" style="font-size: 13px;">
          <div class="row g-2">
            <div class="col-6 text-muted">{{ $t('Subtotal') }}</div>
            <div class="col-6 text-end">{{ $storeIndex.formatThaiCurrency(toNumber(bill.sub_total)) }}</div>

            <template v-if="toNumber(bill.discount) > 0">
              <div class="col-6 text-danger">
                {{ $t('Discount') }}
                <span v-if="bill.discount_reason" class="text-muted">({{ bill.discount_reason }})</span>
              </div>
              <div class="col-6 text-end text-danger">
                - {{ $storeIndex.formatThaiCurrency(toNumber(bill.discount)) }}
              </div>
            </template>

            <template v-if="toNumber(bill.service_charge_amount) > 0">
              <div class="col-6 text-muted">
                {{ $t('Service Charge') }} ({{ toNumber(bill.service_charge_rate) }}%)
              </div>
              <div class="col-6 text-end">
                {{ $storeIndex.formatThaiCurrency(toNumber(bill.service_charge_amount)) }}
              </div>
            </template>

            <template v-if="toNumber(bill.shipping_price) > 0">
              <div class="col-6 text-muted">{{ $t('Shipping') }}</div>
              <div class="col-6 text-end">{{ $storeIndex.formatThaiCurrency(toNumber(bill.shipping_price)) }}</div>
            </template>

            <template v-if="toNumber(bill.total_vat) > 0">
              <div class="col-6 text-muted">
                {{ $t('VAT') }} ({{ toNumber(bill.vat_rate) }}% — {{ bill.tax_policy }})
              </div>
              <div class="col-6 text-end">
                {{ $storeIndex.formatThaiCurrency(toNumber(bill.total_vat)) }}
              </div>
            </template>

            <div class="col-12"><hr class="my-1"></div>

            <div class="col-6 fw-bold">{{ $t('Grand Total') }}</div>
            <div class="col-6 text-end fw-bold fs-6">
              {{ $storeIndex.formatThaiCurrency(toNumber(bill.total)) }}
            </div>

            <template v-if="totalPaid > 0">
              <div class="col-6 text-success">{{ $t('Paid') }}</div>
              <div class="col-6 text-end text-success">- {{ $storeIndex.formatThaiCurrency(totalPaid) }}</div>
            </template>

            <template v-if="toNumber(bill.refunded_amount) > 0">
              <div class="col-6 text-danger">{{ $t('Refunded') }}</div>
              <div class="col-6 text-end text-danger">- {{ $storeIndex.formatThaiCurrency(toNumber(bill.refunded_amount)) }}</div>
            </template>

            <template v-if="outstanding > 0">
              <div class="col-12"><hr class="my-1"></div>
              <div class="col-6 fw-bold text-danger">{{ $t('Outstanding') }}</div>
              <div class="col-6 text-end fw-bold text-danger fs-5">
                {{ $storeIndex.formatThaiCurrency(outstanding) }}
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Payment History -->
      <div v-if="bill.payments && bill.payments.length" class="card m-3 mb-0 bills-card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div>
            <i class="fa-solid fa-credit-card me-2 text-theme" />
            {{ $t('Payment History') }}
          </div>
          <span class="badge bg-light text-dark">{{ bill.payments.length }}</span>
        </div>
        <div class="card-body p-3">
          <div
            v-for="p in bill.payments"
            :key="p.id"
            class="bills-payment-row p-2 rounded-3 mb-2"
          >
            <div class="d-flex justify-content-between">
              <span class="fw-semibold">
                <i :class="methodIcon(p.payment_method_type)" class="me-1 text-theme"/>
                {{ $t(p.payment_method) }}
              </span>
              <span class="fw-bold text-success">
                {{ $storeIndex.formatThaiCurrency(toNumber(p.amount)) }}
              </span>
            </div>
            <div class="text-muted small mt-1">
              <i class="fa-regular fa-clock me-1"/>
              {{ formatDateTime(p.created_at) }}
              <span v-if="p.created_by"> · {{ p.created_by }}</span>
            </div>
            <div v-if="p.reference_no" class="text-muted small">
              {{ $t('Ref.') }} {{ p.reference_no }}
            </div>
            <div v-if="p.note" class="text-muted small fst-italic">
              {{ p.note }}
            </div>
          </div>
        </div>
      </div>

      <!-- Refund Information -->
      <div v-if="hasRefund" class="card m-3 mb-0 bills-card bills-card-danger">
        <div class="card-header text-danger">
          <i class="fa-solid fa-rotate-left me-2" />
          {{ $t('Refund Information') }}
        </div>
        <div class="card-body pb-0">
          <div class="row" style="font-size: 13px;">
            <div class="col-6 col-md-3 mb-3">
              <b>
                <i class="fa-solid fa-circle-info me-1" />
                {{ $t('Refund Status') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-3 mb-3">
              <span class="badge bg-warning">
                {{ $t(bill.refund_status === 'full' ? 'Fully Refunded' : 'Partially Refunded') }}
              </span>
            </div>

            <div class="col-6 col-md-3 mb-3">
              <b>
                <i class="fa-solid fa-coins me-1" />
                {{ $t('Refund Amount') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-3 mb-3 text-danger fw-bold">
              {{ $storeIndex.formatThaiCurrency(toNumber(bill.refunded_amount)) }}
            </div>

            <template v-if="bill.refund_reason">
              <div class="col-6 col-md-3 mb-3">
                <b>
                  <i class="fa-solid fa-comment me-1" />
                  {{ $t('Refund Reason') + ':' }}
                </b>
              </div>
              <div class="col-6 col-md-9 mb-3">
                {{ bill.refund_reason }}
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Void Information -->
      <div v-if="bill.payment_status === 'voided'" class="card m-3 mb-0 bills-card bills-card-muted">
        <div class="card-header text-secondary">
          <i class="fa-solid fa-ban me-2" />
          {{ $t('Void Information') }}
        </div>
        <div class="card-body pb-0">
          <div class="row" style="font-size: 13px;">
            <div class="col-6 col-md-3 mb-3">
              <b>
                <i class="fa-regular fa-calendar-days me-1" />
                {{ $t('Voided At') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-3 mb-3">
              {{ formatDateTime(bill.voided_at) }}
            </div>

            <template v-if="bill.voided_by">
              <div class="col-6 col-md-3 mb-3">
                <b>
                  <i class="fa-solid fa-user-pen me-1" />
                  {{ $t('Voided By') + ':' }}
                </b>
              </div>
              <div class="col-6 col-md-3 mb-3">
                {{ bill.voided_by }}
              </div>
            </template>

            <template v-if="bill.void_reason">
              <div class="col-6 col-md-3 mb-3">
                <b>
                  <i class="fa-solid fa-comment me-1" />
                  {{ $t('Void Reason') + ':' }}
                </b>
              </div>
              <div class="col-6 col-md-9 mb-3">
                {{ bill.void_reason }}
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="m-3"/>
    </div>

    <!-- Footer actions -->
    <div
      v-if="!loading && bill"
      class="bg-light border-top justify-content-center modal-footer py-2"
    >
      <div class="btn-group dropdown">
        <button
          type="button"
          class="btn btn-sm btn-primary dropdown-toggle"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i class="fa-solid fa-ellipsis me-1" />
          {{ $t('More Action') }}
        </button>
        <ul class="dropdown-menu">
          <li>
            <a
              class="dropdown-item"
              href="#"
              @click.prevent="$emit('print')"
            >
              <i class="fa-solid fa-print me-1" />
              {{ $t('Print Bill') }}
            </a>
          </li>
          <li v-if="canCollect">
            <a
              class="dropdown-item text-success"
              href="#"
              @click.prevent="$emit('collect')"
            >
              <i class="fa-solid fa-money-bill-wave me-1" />
              {{ $t('Collect Payment') }}
            </a>
          </li>
          <li v-if="canRefund">
            <a
              class="dropdown-item text-warning"
              href="#"
              @click.prevent="$emit('refund')"
            >
              <i class="fa-solid fa-rotate-left me-1" />
              {{ $t('Refund') }}
            </a>
          </li>
          <li v-if="canVoid"><hr class="dropdown-divider"></li>
          <li v-if="canVoid">
            <a
              class="dropdown-item text-danger"
              href="#"
              @click.prevent="$emit('void')"
            >
              <i class="fa-solid fa-ban me-1" />
              {{ $t('Void Bill') }}
            </a>
          </li>
        </ul>
      </div>
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import moment from 'moment';
import POSPageSkeleton from '@/plugins/POSPageSkeleton.vue';
import { POSBills } from '@/services/endpoint/pos.service';
import { STATUS_COLORS, humanize } from '@/components/bills/constants.js';

const METHOD_ICONS = {
  cash:     'fa-solid fa-money-bill-wave',
  transfer: 'fa-solid fa-qrcode',
  credit:   'fa-solid fa-credit-card',
  card:     'fa-solid fa-credit-card',
  wallet:   'fa-solid fa-wallet',
  voucher:  'fa-solid fa-ticket'
};

export default {
  name: 'POSBillOffcanvasInfo',
  components: { POSPageSkeleton },
  props: {
    billId: { type: [Number, String], required: true }
  },
  emits: ['close', 'print', 'collect', 'refund', 'void'],
  data() {
    return {
      bill: null,
      loading: true
    };
  },
  computed: {
    sourceText() {
      const arr = this.bill?.sources;
      if (!arr || !arr.length) return '-';
      return arr.map(s => this.$t(humanize(s))).join(', ');
    },
    statusLabel() {
      return humanize(this.bill?.payment_status);
    },
    statusColor() {
      return STATUS_COLORS[this.bill?.payment_status] || 'secondary';
    },
    totalPaid() {
      return (this.bill?.payments || []).reduce((s, p) => s + this.toNumber(p.amount), 0);
    },
    totalItemQuantity() {
      return (this.bill?.items || []).reduce((s, i) => s + this.toNumber(i.quantity), 0);
    },
    outstanding() {
      if (!this.bill) return 0;
      const s = this.bill.payment_status;
      if (s === 'paid' || s === 'refunded' || s === 'partially_refunded' || s === 'voided') return 0;
      return Math.max(0, this.toNumber(this.bill.total) - this.totalPaid);
    },
    hasRefund() {
      return this.bill && (this.bill.refund_status === 'partial' || this.bill.refund_status === 'full');
    },
    // บิล platform: เงินจบบนแอปแล้ว — settle/ยกเลิกผ่านหน้า ออเดอร์ Platform เท่านั้น
    canCollect() {
      if (this.bill?.is_platform_bill) return false;
      const s = this.bill?.payment_status;
      return s === 'unpaid' || s === 'partially_paid';
    },
    canRefund() {
      if (!this.bill || this.bill.voided_at || this.bill.is_platform_bill) return false;
      const s = this.bill.payment_status;
      return s === 'paid' || s === 'partially_refunded';
    },
    canVoid() {
      // Void only for unpaid bills — partially_paid / paid must refund first.
      if (!this.bill || this.bill.voided_at || this.bill.is_platform_bill) return false;
      return this.bill.payment_status === 'unpaid';
    }
  },
  async mounted() {
    $(this.$refs.offcanvasInfo).offcanvas('show');
    $(this.$refs.offcanvasInfo).on('hidden.bs.offcanvas', () => this.$emit('close'));
    await this.fetchDetail();
  },
  methods: {
    async fetchDetail() {
      this.loading = true;
      try {
        const res = await POSBills.info(this.billId);
        this.bill = res.data.data;
      } catch (err) {}
      this.loading = false;
    },
    hide() {
      $(this.$refs.offcanvasInfo).offcanvas('hide');
    },
    formatDateTime(value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    },
    methodIcon(type) {
      return METHOD_ICONS[type] || 'fa-solid fa-money-bill';
    },
    toNumber(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    }
  }
};
</script>

<style scoped>
.bills-info-offcanvas {
  width: 560px;
}
.bills-info-offcanvas .offcanvas-body {
  background-color: #f8f9fa;
}

.bills-hero {
  background: linear-gradient(135deg, #fff5e9 0%, #ffe6cf 100%);
  border-bottom: 1px solid #f1d2ab;
}

.bills-card {
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}
.bills-card .card-header {
  background-color: #fff;
  border-bottom: 1px solid #f1f1f1;
  font-weight: 600;
  padding: 0.7rem 1rem;
  font-size: 14px;
}
.bills-card .card-body { background: #fff; }

.bills-card-danger { border-color: #f5d2d4; }
.bills-card-danger .card-header { background: #fff5f5; border-bottom-color: #f5d2d4; }

.bills-card-muted { border-color: #e2e3e5; }
.bills-card-muted .card-header { background: #f8f9fa; }

/* Items */
.bills-item-row {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  font-size: 13px;
  transition: background-color 0.15s ease;
}
.bills-item-row.has-divider {
  border-top: 1px dashed #eef0f2;
}
.bills-item-row:hover {
  background: #fafafa;
}

.bills-item-image {
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
}
.bills-item-image img {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 8px;
  background: #f3f3f3;
}
.bills-item-placeholder {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #f3f3f3;
  color: #adb5bd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.bills-item-qty {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: #2c2c2c;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.bills-item-body {
  flex: 1;
  min-width: 0;
}
.bills-item-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}
.bills-item-name {
  font-weight: 600;
  color: #2c2c2c;
  line-height: 1.3;
  word-break: break-word;
}
.bills-item-total {
  flex-shrink: 0;
  font-weight: 700;
  color: #2c2c2c;
  font-size: 13.5px;
}
.bills-item-sub {
  color: #8a8d92;
  font-size: 11.5px;
  margin-top: 2px;
}
.bills-item-sub-dot {
  margin: 0 5px;
  opacity: 0.6;
}
.bills-item-note {
  margin-top: 4px;
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}
.bills-item-discount {
  margin-top: 4px;
  color: #d63384;
  font-size: 11.5px;
  font-weight: 500;
}

.bills-item-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.bills-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 999px;
  line-height: 1.5;
}
.bills-chip-option {
  background: #fdece0;
  color: #d46010;
  border: 1px solid #f8d4b3;
}
.bills-chip-danger {
  background: #fdeded;
  color: #b02a37;
  border: 1px solid #f5c2c7;
}
.bills-chip-warning {
  background: #fff3cd;
  color: #8a6d3b;
  border: 1px solid #ffe69c;
}

.bills-payment-row {
  background: #f8f9fa;
  border: 1px solid #ececec;
}

.min-w-0 { min-width: 0; }
.fs-7 { font-size: 12px; }
</style>
